import { pluralizeToString } from '~/utils/stringUtils';

import type { PropertyRoomTypeWithImages } from '../ImagesVideo/types';

import type { OfferBuilderRoom } from './types';

export { getRoomKey } from '../utils';

export const REQUIRED_ROOM_FIELDS = [
  { key: 'name', label: 'Name' },
  { key: 'description', label: 'Description' },
  { key: 'bedConfiguration', label: 'Bed configuration' },
  { key: 'category', label: 'Room category' },
] as const;

export type RequiredRoomField = (typeof REQUIRED_ROOM_FIELDS)[number];

export function compareRoomAmenityNames(firstAmenity: string, secondAmenity: string) {
  return firstAmenity.localeCompare(secondAmenity, undefined, { sensitivity: 'base' });
}

export function sortRoomAmenityNames(amenities: Array<string>) {
  return amenities.slice().sort(compareRoomAmenityNames);
}

export function getOfferRoomTypeIds(offerPackages: Array<App.Package>) {
  const roomTypeIds = new Set<string>();

  offerPackages.forEach((offerPackage) => {
    const packageRoomTypeId = 'fk_room_type_id' in offerPackage ? offerPackage.fk_room_type_id : undefined;

    if (typeof packageRoomTypeId === 'string' && packageRoomTypeId) {
      roomTypeIds.add(packageRoomTypeId);
    }

    if ('package_options' in offerPackage && Array.isArray(offerPackage.package_options)) {
      (offerPackage.package_options as unknown as Array<Record<string, unknown>>).forEach((option) => {
        const packageOptionRoomTypeId = option.fk_room_type_id;

        if (typeof packageOptionRoomTypeId === 'string' && packageOptionRoomTypeId) {
          roomTypeIds.add(packageOptionRoomTypeId);
        }
      });
    }
  });

  return roomTypeIds;
}

export function getOfferRoomTypes(
  propertyRoomTypes: Array<PropertyRoomTypeWithImages>,
  offerPackages: Array<App.Package>,
) {
  const offerRoomTypeIds = getOfferRoomTypeIds(offerPackages);

  return propertyRoomTypes.filter((room) => room.id && offerRoomTypeIds.has(room.id));
}

export function getRoomImageCount(room: PropertyRoomTypeWithImages) {
  return (room.images ?? []).filter((image) => image.id_cloudinary_external).length;
}

export function getMissingRequiredRoomFields(room: OfferBuilderRoom): Array<RequiredRoomField> {
  return REQUIRED_ROOM_FIELDS.filter((field) => {
    switch (field.key) {
      case 'name':
        return !room.name?.trim();
      case 'description':
        return !room.description?.trim();
      case 'bedConfiguration':
        return !hasConfiguredBedType(room);
      case 'category':
        return !room.category?.trim();
      default:
        return false;
    }
  });
}

export function getRoomSummary(room: OfferBuilderRoom) {
  const summaryParts = [
    formatBedSummary(room),
    typeof room.size_sqm === 'number' && room.size_sqm > 0 ? `${room.size_sqm}m²` : undefined,
  ].filter(Boolean);

  return summaryParts.length ? summaryParts.join(' · ') : 'Room details missing';
}

export function formatBedType(type?: string) {
  return (type ?? '')
    .replace(/Bed$/, '')
    .replace('DoubleSofa', 'Double Sofa')
    .replace('QueenSofa', 'Queen Sofa')
    .replace('RollAway', 'Roll Away')
    .trim();
}

function hasConfiguredBedType(room: OfferBuilderRoom) {
  if (!room.bed_groups?.length) {
    return false;
  }

  return room.bed_groups.some((group) => group.configuration?.some((configuration) => configuration.type?.trim()));
}

export function formatBedConfiguration(configuration: { quantity?: number; size?: string; type?: string }) {
  if (!configuration.quantity) {
    return undefined;
  }

  const bedType = formatBedType(configuration.type);
  const bedSize = formatBedType(configuration.size);
  const bedLabel = bedType || bedSize;

  return bedLabel ? pluralizeToString(`${bedLabel} Bed`, configuration.quantity) : undefined;
}

export function formatList(items: Array<string>, conjunction: 'and' | 'or') {
  if (items.length <= 1) {
    return items[0];
  }

  return `${items.slice(0, -1).join(', ')} ${conjunction} ${items[items.length - 1]}`;
}

function formatBedSummary(room: OfferBuilderRoom) {
  const bedGroupSummaries = (room.bed_groups ?? [])
    .map((group) => formatList((group.configuration ?? []).map(formatBedConfiguration).filter(Boolean), 'and'))
    .filter(Boolean);

  if (bedGroupSummaries.length > 0) {
    return formatList(bedGroupSummaries, 'or');
  }

  if (typeof room.number_of_beds === 'number') {
    return `${room.number_of_beds} ${room.number_of_beds === 1 ? 'bed' : 'beds'}`;
  }

  if (Array.isArray(room.number_of_beds) && room.number_of_beds.length > 0) {
    const bedCount = room.number_of_beds.reduce((total, count) => total + count, 0);
    return `${bedCount} ${bedCount === 1 ? 'bed' : 'beds'}`;
  }

  return undefined;
}
